import { useEffect, useMemo, useRef, useState } from 'react';
import {
  exportToExcel,
  getPosicoesSalvasPorPlacasData,
  getUltimaPosicaoGeralData
} from '../services/disponibilidadeFrotaPatioApi';
import { emptyFilterOverrides, positionRequestConfig } from '../utils/constants';
import {
  applyFrotaFilters,
  buildGestorOptions,
  buildPlotagemOptions,
  buildSegmentoVeiculoOptions,
  buildTipoOperacaoOptions,
  buildTipoReboqueOptions,
  buildTipoTracaoOptions,
  hasAnyFilledFilter
} from '../utils/filters';
import {
  getCategoriaPorDescricao,
  getCategoriaSincronizavelPorDescricao,
  sortFrotasByDisponibilidade
} from '../utils/mappers';
import {
  buildCachedPositionEntry,
  buildPositionRequestPayload,
  createMissingPositionEntry,
  findPositionBatchItemByPlate,
  getPlateForPosition,
  getPositionEntryFromMap,
  getPositionModalErrorMessage,
  isPositionOlderThanOneDay
} from '../utils/positions';
import { applyCrossSelection, isSameSelection } from '../utils/selection';

export const useDisponibilidadeFrotaPanel = ({
  tipo,
  exportFileName,
  sheetName,
  data,
  optionsData = data,
  filters,
  filterOverrides = emptyFilterOverrides,
  resetFiltrosToken = 0,
  selecaoAtiva,
  onSyncCategoriaAtiva,
  posicoesPorChave = {},
  onPosicaoLoaded,
  onVisiblePageItemsChange
}) => {
  const [filtros, setFiltros] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(25);
  const [posicaoModal, setPosicaoModal] = useState({
    show: false,
    item: null,
    loading: false,
    error: null,
    posicao: null
  });
  const resetFiltrosAnteriorRef = useRef(resetFiltrosToken);
  const podeSelecionar = !selecaoAtiva || selecaoAtiva.tipo === tipo;

  const filterConfig = useMemo(
    () => filters(
      buildGestorOptions(optionsData),
      tipo === 'tracao' ? buildTipoTracaoOptions(optionsData) : buildTipoReboqueOptions(optionsData),
      buildSegmentoVeiculoOptions(optionsData),
      buildTipoOperacaoOptions(optionsData),
      buildPlotagemOptions(optionsData)
    ),
    [filters, optionsData, tipo]
  );
  const filtrosManuaisAtivos = useMemo(() => hasAnyFilledFilter(filtros), [filtros]);
  const filtrosAplicados = useMemo(
    () => ({
      ...(!filtrosManuaisAtivos ? filterOverrides : {}),
      ...filtros
    }),
    [filterOverrides, filtros, filtrosManuaisAtivos]
  );
  const dataFiltrada = useMemo(
    () => sortFrotasByDisponibilidade(
      applyCrossSelection(applyFrotaFilters(data, filtrosAplicados), tipo, selecaoAtiva)
    ).map(item => ({
      ...item,
      coleta: isSameSelection(selecaoAtiva, tipo, item) ? 'Sim' : undefined
    })),
    [data, filtrosAplicados, tipo, selecaoAtiva]
  );
  const currentPageItems = useMemo(() => {
    const pageIndex = Math.max(0, (paginaAtual || 1) - 1);
    const pageSize = quantidadePorPagina || 5;
    const start = pageIndex * pageSize;

    return dataFiltrada.slice(start, start + pageSize);
  }, [dataFiltrada, paginaAtual, quantidadePorPagina]);
  const lastVisiblePageSignatureRef = useRef('');

  useEffect(() => {
    if (resetFiltrosAnteriorRef.current === resetFiltrosToken) return;

    resetFiltrosAnteriorRef.current = resetFiltrosToken;
    setFiltros({});
    setPaginaAtual(1);
  }, [resetFiltrosToken]);

  useEffect(() => {
    if (typeof onVisiblePageItemsChange !== 'function') return;

    const nextSignature = currentPageItems
      .map(item => item?.codigo || item?.protocolo || item?.frota || item?.veiculo || '')
      .join('|');

    if (lastVisiblePageSignatureRef.current === nextSignature) return;

    lastVisiblePageSignatureRef.current = nextSignature;
    onVisiblePageItemsChange(currentPageItems);
  }, [currentPageItems, onVisiblePageItemsChange]);

  const handleApplyFiltros = valores => {
    const modeloSelecionado = valores?.[tipo === 'tracao' ? 'TipoTracao' : 'TipoReboque'];
    const descricaoModelo = modeloSelecionado?.label || modeloSelecionado?.value || modeloSelecionado;
    const categoriaOriginalModelo = getCategoriaPorDescricao(descricaoModelo);
    const categoriaModelo = getCategoriaSincronizavelPorDescricao(descricaoModelo);

    if (onSyncCategoriaAtiva) {
      if (categoriaModelo && categoriaModelo !== 'outros') {
        onSyncCategoriaAtiva(categoriaModelo);
      } else if (categoriaOriginalModelo === 'cavalo') {
        onSyncCategoriaAtiva(null);
      }
    }

    setFiltros(valores || {});
    setPaginaAtual(1);
  };

  const handleClosePosicao = () => {
    setPosicaoModal(prev => ({
      ...prev,
      show: false
    }));
  };

  const handleOpenPosicao = async item => {
    const placa = getPlateForPosition(item);
    const cached = getPositionEntryFromMap(posicoesPorChave, item);
    const posicaoAtual = item?.posicao || cached?.posicao || null;

    if (posicaoAtual) {
      setPosicaoModal({
        show: true,
        item,
        loading: false,
        error: null,
        posicao: posicaoAtual
      });
      return;
    }

    setPosicaoModal({
      show: true,
      item,
      loading: Boolean(placa),
      error: placa ? null : 'Nao foi possivel identificar a placa desta frota.',
      posicao: null
    });

    if (!placa) return;

    try {
      const response = await getPosicoesSalvasPorPlacasData([placa], positionRequestConfig);
      const nextEntry = buildCachedPositionEntry(
        findPositionBatchItemByPlate(response, placa) || { placa, encontrada: false },
        placa
      );

      onPosicaoLoaded?.(item, nextEntry);

      if (!nextEntry.posicao) {
        setPosicaoModal(prev => ({
          ...prev,
          loading: false,
          error: 'Localizacao nao encontrada para a placa informada.',
          posicao: null
        }));
        return;
      }

      setPosicaoModal(prev => ({
        ...prev,
        loading: false,
        error: null,
        posicao: nextEntry.posicao
      }));
    } catch (error) {
      onPosicaoLoaded?.(item, createMissingPositionEntry());
      setPosicaoModal(prev => ({
        ...prev,
        loading: false,
        error: getPositionModalErrorMessage(error),
        posicao: null
      }));
    }
  };

  const handleOpenCardPosicao = async item => {
    const requestPayload = buildPositionRequestPayload(item);
    const hasRequestPayload = Boolean(requestPayload?.idVeiculo || requestPayload?.placa || requestPayload?.frota);

    setPosicaoModal({
      show: true,
      item,
      loading: hasRequestPayload,
      error: hasRequestPayload ? null : 'Nao foi possivel identificar os dados desta frota para consultar a posicao.',
      posicao: null
    });

    if (!hasRequestPayload) return;

    try {
      const response = await getUltimaPosicaoGeralData(requestPayload, positionRequestConfig);
      const nextEntry = buildCachedPositionEntry(
        response,
        requestPayload.placa || getPlateForPosition(item)
      );

      onPosicaoLoaded?.(item, nextEntry);

      if (!nextEntry.posicao) {
        setPosicaoModal(prev => ({
          ...prev,
          loading: false,
          error: 'Localizacao nao encontrada para a frota informada.',
          posicao: null
        }));
        return;
      }

      setPosicaoModal(prev => ({
        ...prev,
        loading: false,
        error: null,
        posicao: nextEntry.posicao
      }));
    } catch (error) {
      onPosicaoLoaded?.(item, createMissingPositionEntry());
      setPosicaoModal(prev => ({
        ...prev,
        loading: false,
        error: getPositionModalErrorMessage(error),
        posicao: null
      }));
    }
  };

  const getPositionStatus = item => {
    const placa = getPlateForPosition(item);
    const cached = getPositionEntryFromMap(posicoesPorChave, item);

    if (!placa) return 'missing';
    if (!cached) return 'unknown';
    if (cached.status === 'found' && isPositionOlderThanOneDay(cached.dataPosicao)) return 'stale';
    return cached.status || 'unknown';
  };

  const getPositionTooltip = item => {
    const placa = getPlateForPosition(item);
    const cached = getPositionEntryFromMap(posicoesPorChave, item);

    if (!placa) return 'Placa nao informada';
    if (cached?.local) return `Local: ${cached.local}`;
    if (cached?.status === 'missing') return 'Posicao nao encontrada';
    if (cached?.status === 'loading') return 'Consultando posicao...';
    return 'Ver posicao';
  };

  const handleExportar = async () => {
    const exportRows = dataFiltrada.map(item => ({
      ID: item.id,
      Frota: item.frota,
      Veiculo: item.veiculo,
      Gestor: item.gestor,
      'Modelo Veicular': item.modeloVeicular,
      Plotagem: item.plotagem,
      'Data Inicio Carregamento': item.dataInicioCarregamento,
      'Segmento Veiculo': item.segmentoVeiculo,
      'Tipo da Operacao': item.tipoOperacao,
      Cargas: item.cargasResumo,
      'ID Viagem': item.viagensResumo,
      'Status Viagem': item.statusViagensResumo,
      'Status': item.disponivelLabel,
      'Status Patio': item.statusPatioLabel
    }));

    await exportToExcel(exportRows, exportFileName, sheetName);
  };

  return {
    filterConfig,
    filtrosAplicados,
    dataFiltrada,
    paginaAtual,
    quantidadePorPagina,
    setPaginaAtual,
    setQuantidadePorPagina,
    handleApplyFiltros,
    handleExportar,
    handleOpenPosicao,
    handleOpenCardPosicao,
    handleClosePosicao,
    getPositionStatus,
    getPositionTooltip,
    posicaoModal,
    podeSelecionar
  };
};
